import { Command } from 'commander';
import chalk from 'chalk';
import { join, dirname, basename } from 'path';
import { existsSync, readFileSync, readdirSync } from 'fs';
import { logger } from '../lib/logger.js';

interface ManifestEntry {
  slug: string;
  icon?: string;
  title?: string | { fr?: string; en?: string };
}

// __dirname is 'dist' after bundling, so go up one level
function docsRoot(): string {
  return join(dirname(__dirname), '.documentation');
}

/**
 * The generator (`npm run build:docs`) writes `.documentation/manifest.json`
 * listing every page in sidebar order — the single source of truth for what
 * `ss docs` can open. Falls back to scanning `*.html` for older installs
 * shipped without a manifest.
 */
function loadPages(): ManifestEntry[] {
  const root = docsRoot();
  try {
    const raw = JSON.parse(readFileSync(join(root, 'manifest.json'), 'utf-8')) as {
      pages?: ManifestEntry[];
    };
    if (Array.isArray(raw.pages) && raw.pages.length > 0) return raw.pages;
  } catch {
    // fall through to filesystem scan
  }
  try {
    return readdirSync(root)
      .filter((f) => f.endsWith('.html'))
      .map((f) => ({ slug: basename(f, '.html') }));
  } catch {
    return [];
  }
}

function labelOf(entry: ManifestEntry): string {
  const title = entry.title;
  if (typeof title === 'string') return title;
  return title?.fr || title?.en || entry.slug;
}

export const docsCommand = new Command('docs')
  .alias('d')
  .description('Open SmartStack documentation in browser')
  .argument('[page]', 'Documentation page to open', 'index')
  .option('-l, --list', 'List available documentation pages')
  .action(async (page: string, options) => {
    const pages = loadPages();

    if (pages.length === 0) {
      logger.error('Documentation not found');
      logger.info(`Expected at: ${docsRoot()}`);
      return;
    }

    if (options.list) {
      logger.header('Available Documentation Pages');
      console.log();
      for (const p of pages) {
        const suffix = p.slug === 'index' ? `${labelOf(p)} (home)` : labelOf(p);
        console.log(`  ${chalk.cyan('smartstack docs')} ${chalk.yellow(p.slug)}  ${chalk.gray('→')} ${p.icon ? `${p.icon} ` : ''}${suffix}`);
      }
      console.log();
      return;
    }

    const pageName = page.replace('.html', '');

    if (!pages.some((p) => p.slug === pageName)) {
      logger.error(`Unknown page: ${page}`);
      console.log(`Available pages: ${pages.map((p) => p.slug).join(', ')}`);
      return;
    }

    const docsPath = join(docsRoot(), `${pageName}.html`);

    if (!existsSync(docsPath)) {
      logger.error('Documentation not found');
      logger.info(`Expected at: ${docsPath}`);
      return;
    }

    logger.info(`Opening ${chalk.cyan(pageName)} documentation...`);

    const { exec } = await import('child_process');
    const fileUrl = `file:///${docsPath.replace(/\\/g, '/')}`;

    const command = process.platform === 'win32'
      ? `start "" "${fileUrl}"`
      : process.platform === 'darwin'
        ? `open "${fileUrl}"`
        : `xdg-open "${fileUrl}"`;

    exec(command, (error) => {
      if (error) {
        logger.error(`Failed to open browser: ${error.message}`);
        logger.info(`Manual path: ${docsPath}`);
      }
    });
  });
